import { lazy } from "react";
import { createBrowserRouter, RouterProvider } from "react-router-dom";

import { ErrorBoundary } from "@/shared/components";
import ProtectedRoute from "@/shared/components/layout/ProtectedRoute/ProtectedRoute";
import config from "../config";
import { paths } from "../shared/utils/routing";
import { RootLayout } from "./layout/RootLayout";

// Lazy-loaded components
const AdminDashboard = lazy(() =>
  import("@/features/admin").then((module) => ({
    default: module.AdminDashboard,
  }))
);
const ModelViewPage = lazy(() =>
  import("@/features/models/pages/ModelViewPage").then((module) => ({
    default: module.ModelViewPage,
  }))
);
const ModelEditPage = lazy(
  () => import("@/features/models/pages/ModelEditPage")
);
const CreateModelPage = lazy(
  () => import("@/features/models/pages/CreateModelPage")
);
const AreaEditPage = lazy(() =>
  import("@/features/areas/pages/AreaEditPage").then((module) => ({
    default: module.AreaEditPage,
  }))
);
const AreaCreatePage = lazy(() =>
  import("@/features/areas/pages/AreaCreatePage").then((module) => ({
    default: module.AreaCreatePage,
  }))
);
const AreaViewPage = lazy(() =>
  import("@/features/areas/pages/AreaViewPage").then((module) => ({
    default: module.AreaViewPage,
  }))
);
const CreateRoutePage = lazy(
  () => import("@/features/routes/pages/CreateRoutePage")
);
const GoogleCallback = lazy(
  () => import("../features/auth/pages/google-callback/GoogleCallback")
);
const OnboardingPage = lazy(() =>
  import("../features/auth/pages/onboarding").then((module) => ({
    default: module.OnboardingPage,
  }))
);
const ProfilePage = lazy(() =>
  import("../features/profile").then((module) => ({
    default: module.ProfilePage,
  }))
);
const UserAscentsPage = lazy(() =>
  import("../features/ascents").then((module) => ({
    default: module.UserAscentsPage,
  }))
);
const BookmarksPage = lazy(() =>
  import("../features/bookmarks").then((module) => ({
    default: module.BookmarksPage,
  }))
);
const _FeedPage = lazy(() => import("../features/social/pages/FeedPage"));
const _UserSearchPage = lazy(
  () => import("../features/social/pages/UserSearchPage")
);
const _FriendsListPage = lazy(
  () => import("../features/social/pages/FriendsListPage")
);
const CommunityPage = lazy(
  () => import("../features/social/pages/CommunityPage")
);
const NotificationsPage = lazy(
  () => import("../features/social/pages/NotificationsPage")
);
const SettingsPage = lazy(() =>
  import("../features/settings").then((module) => ({
    default: module.SettingsPage,
  }))
);
const SubscriptionPage = lazy(() =>
  import("../features/settings/pages/SubscriptionPage").then((module) => ({
    default: module.SubscriptionPage,
  }))
);
const TeamViewPage = lazy(() =>
  import("../features/teams/pages/TeamViewPage").then((module) => ({
    default: module.TeamViewPage,
  }))
);
const TeamEditPage = lazy(() =>
  import("../features/teams/pages/TeamEditPage").then((module) => ({
    default: module.TeamEditPage,
  }))
);
const TeamsListPage = lazy(() =>
  import("../features/teams/pages/TeamsListPage").then((module) => ({
    default: module.TeamsListPage,
  }))
);
const RequestResetPassword = lazy(
  () => import("../features/auth/pages/request-reset-password")
);
const SignIn = lazy(() => import("../features/auth/pages/signin"));
const SignInWithEmail = lazy(
  () => import("../features/auth/pages/signin-with-email")
);
const SignUp = lazy(() => import("../features/auth/pages/signup"));
const SignUpWithEmail = lazy(
  () => import("../features/auth/pages/signup-with-email")
);
const HomePage = lazy(() => import("./pages/home-page/HomePage"));
const LandingPage = lazy(() => import("./pages/landing-page/LandingPage"));
const PricingPage = lazy(() => import("./pages/pricing-page/PricingPage"));
const FeedbackPage = lazy(() =>
  import("../features/feedback/pages").then((module) => ({
    default: module.FeedbackPage,
  }))
);
const LegalPage = lazy(() =>
  import("./pages/legal").then((module) => ({ default: module.LegalPage }))
);
const TermsOfServicePage = lazy(() =>
  import("./pages/legal").then((module) => ({
    default: module.TermsOfServicePage,
  }))
);
const PrivacyPolicyPage = lazy(() =>
  import("./pages/legal").then((module) => ({
    default: module.PrivacyPolicyPage,
  }))
);
const LicensesPage = lazy(() =>
  import("./pages/legal").then((module) => ({
    default: module.LicensesPage,
  }))
);
const MapView = lazy(() =>
  import("../features/map").then((module) => ({ default: module.MapView }))
);

// Documentation pages
const DocsPage = lazy(() =>
  import("./pages/docs").then((module) => ({ default: module.DocsPage }))
);
const ForClimbersPage = lazy(() =>
  import("./pages/docs").then((module) => ({ default: module.ForClimbersPage }))
);
const ForContentCreatorsPage = lazy(() =>
  import("./pages/docs").then((module) => ({
    default: module.ForContentCreatorsPage,
  }))
);
const AddingAreasPage = lazy(() =>
  import("./pages/docs").then((module) => ({ default: module.AddingAreasPage }))
);
const AddingModelsPage = lazy(() =>
  import("./pages/docs").then((module) => ({
    default: module.AddingModelsPage,
  }))
);

const RouteErrorBoundary = () => {
  return (
    <ErrorBoundary
      fallbackComponent={({ error, resetError }) => (
        <div style={{ padding: "2rem", textAlign: "center" }}>
          <h1>Oops! Something went wrong</h1>
          <p>
            {error?.message || "The page you're looking for doesn't exist."}
          </p>
          <button
            type="button"
            onClick={resetError}
            style={{ marginTop: "1rem", padding: "0.5rem 1rem" }}
          >
            Go Back
          </button>
        </div>
      )}
    >
      <div>Route Error</div>
    </ErrorBoundary>
  );
};

// Build protected routes array
const buildProtectedRoutes = () => {
  const routes = [
    {
      path: paths.home,
      element: <HomePage />,
    },
    {
      path: paths.map,
      element: <MapView />,
    },
    {
      path: paths.profile,
      element: <ProfilePage />,
    },
    {
      path: paths.profileAscents,
      element: <UserAscentsPage />,
    },
    {
      path: paths.bookmarks,
      element: <BookmarksPage />,
    },
    {
      path: paths.social.community,
      element: <CommunityPage />,
    },
    {
      path: paths.social.feed,
      element: <CommunityPage />, // redirect or reuse
    },
    {
      path: paths.social.search,
      element: <_UserSearchPage isFullPage={true} />,
    },
    {
      path: paths.social.friends,
      element: <_FriendsListPage />,
    },
    {
      path: paths.social.notifications,
      element: <NotificationsPage />,
    },
    {
      path: paths.publicProfile(),
      element: <ProfilePage />,
    },
    {
      path: paths.settings,
      element: <SettingsPage />,
    },
    {
      path: paths.feedback,
      element: <FeedbackPage />,
    },
    {
      path: paths.teams.view(),
      element: <TeamViewPage />,
    },
    {
      path: paths.teams.edit(),
      element: <TeamEditPage />,
    },
    {
      path: paths.admin.home,
      element: <AdminDashboard />,
    },
    {
      path: paths.admin.content.areas.new,
      element: <AreaCreatePage />,
    },
    {
      path: paths.admin.content.areas.edit(),
      element: <AreaEditPage />,
    },
    {
      path: paths.admin.content.areas.view(),
      element: <AreaViewPage />,
    },
    {
      path: paths.admin.content.models.new,
      element: <CreateModelPage />,
    },
    {
      path: paths.admin.content.models.edit(),
      element: <ModelEditPage />,
    },
    {
      path: paths.admin.content.models.view(),
      element: <ModelViewPage />,
    },
    {
      path: paths.admin.content.models.viewInArea(),
      element: <ModelViewPage />,
    },
    {
      path: paths.admin.content.routes.new,
      element: <CreateRoutePage />,
    },
    {
      path: paths.teams.list,
      element: <TeamsListPage />,
    },
  ];

  // Conditionally add subscription route
  if (config.FEATURE_FLAG_SHOW_PAYMENTS) {
    routes.push({
      path: paths.subscription,
      element: <SubscriptionPage />,
    });
  }

  return routes;
};

const router = createBrowserRouter([
  {
    path: paths.landing,
    element: <LandingPage />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: paths.pricing,
    element: <PricingPage />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: paths.docs.main,
    element: <DocsPage />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: paths.docs.forClimbers,
    element: <ForClimbersPage />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: paths.docs.forContentCreators,
    element: <ForContentCreatorsPage />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: paths.docs.addingAreas,
    element: <AddingAreasPage />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: paths.docs.addingModels,
    element: <AddingModelsPage />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    element: (
      <ProtectedRoute>
        <RootLayout />
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
    children: buildProtectedRoutes(),
  },
  {
    path: paths.legal.index,
    element: <LegalPage />,
  },
  {
    path: paths.legal.terms,
    element: <TermsOfServicePage />,
  },
  {
    path: paths.legal.privacy,
    element: <PrivacyPolicyPage />,
  },
  {
    path: paths.legal.licenses,
    element: <LicensesPage />,
  },
  {
    path: paths.signin,
    element: <SignIn />,
  },
  {
    path: paths.googleCallback,
    element: <GoogleCallback />,
  },
  {
    path: paths.emailSignIn,
    element: <SignInWithEmail />,
  },
  {
    path: paths.signup,
    element: <SignUp />,
  },
  {
    path: paths.signUpWithEmail,
    element: <SignUpWithEmail />,
  },
  {
    path: paths.requestResetPassword,
    element: <RequestResetPassword />,
  },
  {
    path: paths.onboarding,
    element: <OnboardingPage />,
  },
]);

function App() {
  return <RouterProvider router={router} />;
}

export default App;
